import React, { useState, useEffect } from 'react';
import { getImageEnhancementsForStructures } from '../services/structureService';
import '../styles/AIRenderingsGrid.css';
import '../styles/ProjectDetail.css'; // For documents-placeholder styling

function AIRenderingsGrid({ structures, isViewOnly = false }) {
  const [enhancements, setEnhancements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [groupBy, setGroupBy] = useState('structure'); // 'style', 'structure', 'all'

  useEffect(() => {
    loadEnhancements();
  }, [structures]);

  const loadEnhancements = async () => {
    try {
      setLoading(true);
      setError(null);

      // Extract structure IDs from the structures array
      const structureIds = structures.map((s) => s.id);

      if (structureIds.length === 0) {
        setEnhancements([]);
        return;
      }

      // Fetch all enhancements for these structures
      const enhancementData =
        await getImageEnhancementsForStructures(structureIds);
      setEnhancements(enhancementData);
    } catch (err) {
      console.error('Error loading enhancements:', err);
      setError('Failed to load renderings');
    } finally {
      setLoading(false);
    }
  };

  // Group enhancements by style
  const groupEnhancementsByStyle = () => {
    const grouped = {};
    enhancements.forEach((enhancement) => {
      const style = enhancement.style || 'default';
      if (!grouped[style]) {
        grouped[style] = [];
      }
      grouped[style].push(enhancement);
    });
    return grouped;
  };

  // Get structure name by ID
  const getStructureName = (structureId) => {
    const structure = structures.find((s) => s.id === structureId);
    return structure?.name || structure?.structureName || 'Unknown Room';
  };

  const handleImageClick = (enhancement) => {
    setSelectedImage(enhancement);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className="ai-renderings-loading">
        <p>Loading renderings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-renderings-error">
        <p>{error}</p>
      </div>
    );
  }

  if (enhancements.length === 0) {
    return (
      <div className="documents-placeholder">
        <p>No renderings yet</p>
        {!isViewOnly && (
          <p className="documents-hint">
            Generate renderings from the room view
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="ai-renderings-container">
      <div className="ai-renderings-controls">
        <div className="enhancement-count">
          {enhancements.length} rendering{enhancements.length !== 1 ? 's' : ''}
        </div>
        <div className="group-by-controls">
          <span>Group by:</span>
          <button
            className={groupBy === 'all' ? 'active' : ''}
            onClick={() => setGroupBy('all')}
          >
            All
          </button>
          <button
            className={groupBy === 'style' ? 'active' : ''}
            onClick={() => setGroupBy('style')}
          >
            Style
          </button>
          <button
            className={groupBy === 'structure' ? 'active' : ''}
            onClick={() => setGroupBy('structure')}
          >
            Room
          </button>
        </div>
      </div>

      {groupBy === 'all' && (
        <div className="ai-renderings-grid">
          {enhancements.map((enhancement) => (
            <div
              key={enhancement.id}
              className="ai-rendering-card"
              onClick={() => handleImageClick(enhancement)}
            >
              <img
                src={enhancement.enhancedUrl}
                alt={`${enhancement.style} rendering`}
                loading="lazy"
              />
              <div className="ai-rendering-info">
                <p className="ai-rendering-style">{enhancement.style}</p>
                <p className="ai-rendering-room">
                  {getStructureName(enhancement.structureId)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {groupBy === 'style' && (
        <div className="ai-renderings-grouped">
          {Object.entries(groupEnhancementsByStyle()).map(([style, items]) => (
            <div key={style} className="ai-rendering-group">
              <h3>{style.charAt(0).toUpperCase() + style.slice(1)}</h3>
              <div className="ai-renderings-grid">
                {items.map((enhancement) => (
                  <div
                    key={enhancement.id}
                    className="ai-rendering-card"
                    onClick={() => handleImageClick(enhancement)}
                  >
                    <img
                      src={enhancement.enhancedUrl}
                      alt={`${enhancement.style} rendering`}
                      loading="lazy"
                    />
                    <div className="ai-rendering-info">
                      <p className="ai-rendering-room">
                        {getStructureName(enhancement.structureId)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {groupBy === 'structure' && (
        <div className="ai-renderings-grouped">
          {structures.map((structure) => {
            const structureId = structure.id;
            const structureEnhancements = enhancements.filter(
              (e) => e.structureId === structureId
            );

            if (structureEnhancements.length === 0) return null;

            return (
              <div key={structureId} className="ai-rendering-group">
                <h3>{getStructureName(structureId)}</h3>
                <div className="ai-renderings-grid">
                  {structureEnhancements.map((enhancement) => (
                    <div
                      key={enhancement.id}
                      className="ai-rendering-card"
                      onClick={() => handleImageClick(enhancement)}
                    >
                      <img
                        src={enhancement.enhancedUrl}
                        alt={`${enhancement.style} rendering`}
                        loading="lazy"
                      />
                      <div className="ai-rendering-info">
                        <p className="ai-rendering-style">
                          {enhancement.style}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedImage && (
        <div className="ai-rendering-modal" onClick={closeModal}>
          <div
            className="ai-rendering-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-modal" onClick={closeModal}>
              ×
            </button>
            <img
              src={selectedImage.enhancedUrl}
              alt={`${selectedImage.style} rendering`}
            />
            <div className="modal-info">
              <h3>{selectedImage.style}</h3>
              <p>{getStructureName(selectedImage.structureId)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIRenderingsGrid;
